<template>
  <div class="screenshot-wrapper relative">
    <!-- Action Button (Optional - can be placed elsewhere via slot) -->
    <div v-if="showButton" class="screenshot-actions mb-2" :class="buttonPosition">
      <v-btn
        :color="buttonColor"
        :small="smallButton"
        :loading="isCapturing"
        class="normal-case rounded-lg"
        @click="captureScreenshot"
      >
        <v-icon left small>{{ buttonIcon }}</v-icon>
        {{ buttonText }}
      </v-btn>
    </div>

    <!-- The content to capture -->
    <div ref="captureArea" class="capture-area">
      <slot></slot>
    </div>

    <!-- External trigger slot if user wants to use their own button -->
    <slot name="trigger" :capture="captureScreenshot" :loading="isCapturing"></slot>
  </div>
</template>

<script>
import html2canvas from 'html2canvas'

export default {
  name: 'ScreenshotWrapper',
  props: {
    fileName: {
      type: String,
      default: 'screenshot'
    },
    buttonText: {
      type: String,
      default: 'Take Screenshot'
    },
    buttonIcon: {
      type: String,
      default: 'mdi-camera'
    },
    buttonColor: {
      type: String,
      default: 'primary'
    },
    buttonPosition: {
      type: String,
      default: 'justify-end' // justify-start, justify-center, justify-end
    },
    showButton: {
      type: Boolean,
      default: true
    },
    smallButton: {
      type: Boolean,
      default: true
    },
    scale: {
      type: Number,
      default: 2
    },
    quality: {
      type: Number,
      default: 1
    },
    autoDownload: {
      type: Boolean,
      default: true
    },
    showWatermark: {
      type: Boolean,
      default: false
    },
    watermarkText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isCapturing: false
    }
  },
  methods: {
    async captureScreenshot() {
      if (this.isCapturing) return
      
      this.isCapturing = true
      this.$emit('started')

      try {
        await this.$nextTick()
        
        const element = this.$refs.captureArea
        if (!element) throw new Error('Capture area not found')

        let canvas = await html2canvas(element, {
          useCORS: true,
          scale: this.scale,
          backgroundColor: '#ffffff',
          logging: false,
          allowTaint: true
        })

        // Add Watermark if enabled
        if (this.showWatermark) {
          // Create a secondary canvas to avoid any context issues with the one from html2canvas
          const watermarkedCanvas = document.createElement('canvas')
          watermarkedCanvas.width = canvas.width
          watermarkedCanvas.height = canvas.height
          const wCtx = watermarkedCanvas.getContext('2d')

          // Draw original capture
          wCtx.drawImage(canvas, 0, 0)

          const scale = this.scale || 2
          const fontSize = 14 * scale
          const barHeight = fontSize + (15 * scale)
          
          // Get user info and timestamp from storage object provided
          const userData = this.$storage ? this.$storage.getUniversal('user') : null
          let userName = 'Staff'
          let mobile = ''
          
          if (userData) {
            const fName = userData.first_name || userData.user?.first_name || ''
            const lName = userData.last_name || userData.user?.last_name || ''
            userName = `${fName} ${lName}`.trim() || 'Staff'
            mobile = userData.mobile || userData.user?.mobile || ''
          }
          
          const timestamp = this.$dayjs ? this.$dayjs().format('DD/MM/YYYY hh:mm:ss A') : new Date().toLocaleString()
          const infoText = `Logged By: ${userName} (${mobile}) | Time: ${timestamp}`

          // Draw BOTTOM BAR only
          wCtx.fillStyle = '#000000' // Black Background
          wCtx.fillRect(0, canvas.height - barHeight, canvas.width, barHeight)
          
          wCtx.fillStyle = '#ffffff' // White Text
          wCtx.font = `bold ${fontSize}px sans-serif`
          wCtx.textAlign = 'center'
          wCtx.textBaseline = 'middle'
          wCtx.fillText(infoText, canvas.width / 2, canvas.height - (barHeight / 2))

          // Replace the canvas with the watermarked one
          canvas = watermarkedCanvas
        }

        const dataUrl = canvas.toDataURL('image/png', this.quality)
        
        if (this.autoDownload) {
          const link = document.createElement('a')
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
          link.download = `${this.fileName}_${timestamp}.png`
          link.href = dataUrl
          link.click()
        }

        this.$emit('success', dataUrl)
        return dataUrl
      } catch (err) {
        console.error('Screenshot capture failed:', err)
        this.$emit('error', err)
        throw err
      } finally {
        this.isCapturing = false
        this.$emit('finished')
      }
    }
  }
}
</script>

<style scoped>
.screenshot-wrapper {
  width: 100%;
}

.screenshot-actions {
  display: flex;
  width: 100%;
}

.capture-area {
  width: 100%;
  background: white; /* Ensure background is captured correctly */
}
</style>
